import {
  isPrefilledWeightSource,
  nextUnit,
  type DropsetStepDraft,
} from '@heraion/shared';
import { convertWeightText } from '../../convertWeightText';
import {
  emptySet,
  failToggleFields,
  initial,
  insertAt,
  removeAt,
  replaceAt,
  seedInsertedSet,
  seedNextSet,
  withEntry,
  withSet,
} from './loggerDraftHelpers';
import type { LoggerAction, LoggerDraft } from './loggerDraftTypes';
import { kindNeedsDuration } from '../deriveEntryKind';
import { noteEventReducer } from './noteEventReducer';
import { loggerGroupReducer } from './loggerGroupReducer';
import { loggerSessionReducer } from './loggerSessionReducer';
import { hydrateDraft } from './hydrateDraft';

// The nested-draft reducer: every Logger action expressed against the
// hierarchical LoggerDraft (entries[].sets[]). This is the battle-tested
// logic the live flat store reuses verbatim — loggerDraft.ts wraps it with
// flattenDraft/assembleDraftKeepIds so a flat WorkoutState reduces through
// these exact cases (see loggerDraft.ts flatReducer). Extracted from
// loggerDraft.ts when adding the flat store pushed that file over the 500-line
// budget; behaviour unchanged.

export function loggerReducer(
  state: LoggerDraft,
  action: LoggerAction,
): LoggerDraft {
  // Timeline note events live on a separate slice (state.noteEvents) and never
  // touch entries[].sets[]; delegate them so this switch stays focused on the
  // set/entry model. The early return narrows `action` so the switch below
  // stays exhaustive over the remaining union (TS discriminated-union narrowing).
  if (
    action.type === 'add_note_event' ||
    action.type === 'update_note_event' ||
    action.type === 'remove_note_event'
  ) {
    return noteEventReducer(state, action);
  }
  // Superset/circuit GROUP actions go the same way, for the same reason and
  // by the same mechanism (EQ-1, 2026-08-13, when this file crossed 500):
  // each of them keys on `entry.group?.id` and reasons about the group as a
  // whole, while the switch below reasons about one entry or one set. That
  // line already existed; the delegation only makes it a file boundary.
  // Spelled out rather than tested against a Set of names: a Set lookup is
  // opaque to TS, which would leave the switch below non-exhaustive and
  // force a cast here — and a cast is exactly where a missed action would
  // hide. This disjunction narrows, so both halves stay checked.
  if (
    action.type === 'add_round_to_group' ||
    action.type === 'remove_round_from_group' ||
    action.type === 'align_group_sets' ||
    action.type === 'set_group' ||
    action.type === 'split_group' ||
    action.type === 'remove_group_member' ||
    action.type === 'move_group_member' ||
    action.type === 'toggle_group_effort'
  ) {
    return loggerGroupReducer(state, action);
  }
  // The SESSION's own fields go the same way, for the same reason (LG-77,
  // 2026-09-08, when this file was found 16 lines over the cap): each of them
  // writes a field of the workout itself, while the switch below reasons about
  // one entry or one set. Spelled out rather than tested against a Set, like
  // the two above: a disjunction narrows, a Set lookup would force a cast.
  if (
    action.type === 'set_name' ||
    action.type === 'set_location' ||
    action.type === 'set_started_at' ||
    action.type === 'set_ended_at' ||
    action.type === 'start_today_draft'
  ) {
    return loggerSessionReducer(state, action);
  }
  switch (action.type) {
    case 'add_exercise':
      return {
        ...state,
        entries: [
          ...state.entries,
          {
            exerciseId: action.exerciseId,
            exerciseName: action.exerciseName,
            isBodyweight: action.isBodyweight,
            movementPattern: action.movementPattern,
            group: null,
            // EQ-1: already resolved by the caller, or absent. The reducer
            // never decides which machine — it only records the answer.
            equipmentInstance: action.equipmentInstance ?? null,
            location: action.location ?? null,
            // Default to bilateral (multiplier 1): the entered weight is
            // the total load. LoggerScreen seeds this to 2 (unilateral)
            // for exercises flagged Exercise.unilateral, and the
            // entry-header chip flips it as a manual override (sticky
            // preference seeded from AsyncStorage at the LoggerScreen
            // layer).
            loadedSides: 1,
            isCardio: action.isCardio,
            isTimeBased: action.isTimeBased,
            // Effort tracking ON by default for everything EXCEPT cardio and
            // timed holds (opt-out per entry). Bodyweight included: RIR
            // ("reps left in the tank") is just as meaningful on pull-ups.
            effortColumn: !action.isCardio && !action.isTimeBased,
            historyOpen: false,
            sets: [emptySet(action.defaultUnit, 1)],
          },
        ],
      };
    case 'remove_exercise':
      return {
        ...state,
        entries: removeAt(state.entries, action.entryIdx),
      };
    case 'replace_exercise':
      return withEntry(state, action.entryIdx, (entry) => {
        // A cross-kind swap (reps-family ↔ duration-family) orphans the set's
        // already-typed field, which would be silently dropped at save (IB-6).
        // Reset the sets to blank so nothing is lost quietly — the UI confirms
        // first when there's logged data to clear. Same-kind swaps keep the
        // logged sets (panchina occupata: bench → db bench).
        const crosses =
          kindNeedsDuration(entry) !== kindNeedsDuration(action);
        // EQ-1-TAGSTART — the same action re-stamps a MACHINE on the exercise
        // already there (§8.4: naming a machine is a same-exercise replace,
        // one gesture, not two). Then nothing about the sets is stale: the
        // prescription, the prescribed effort and the athlete's note all still
        // describe this exercise, because it is still this exercise. The reset
        // below exists because the exercise CHANGED — so it must not run when
        // it did not. Inbox f2881eaa (2026-08-20): naming a machine emptied
        // both the prescribed and the history weight, and the athlete was left
        // with no starting load exactly where they needed one.
        const isSameExercise = action.exerciseId === entry.exerciseId;
        // The catalog-tag snapshot describes the old exercise: it goes with it.
        const { tags: oldTags, ...kept } = entry;
        return {
          ...kept,
          ...(isSameExercise && oldTags ? { tags: oldTags } : {}),
          exerciseId: action.exerciseId,
          exerciseName: action.exerciseName,
          isBodyweight: action.isBodyweight,
          // Adopt the new exercise's logging kind so the UI (hold timer vs
          // reps/weight vs cardio) and the saved set kind follow the swap.
          // IB-2: previously stale → treadmill→hold stayed cardio, weighted→
          // hold stayed weighted.
          isCardio: action.isCardio,
          isTimeBased: action.isTimeBased,
          movementPattern: action.movementPattern,
          // EQ-1: the swap picker is the one surface that asks which
          // machine. `undefined` means it had nothing to say (no instances
          // known), and must NOT wipe an instance the entry already
          // carries — the athlete swapping bench→db-bench is not telling
          // us the machine became unknown.
          equipmentInstance:
            action.equipmentInstance === undefined
              ? entry.equipmentInstance
              : action.equipmentInstance,
          // IB-8: adopt the new exercise's laterality seed too — same
          // completed-sets-stay-honest rule as set_entry_loaded_sides.
          loadedSides: action.loadedSides,
          // IB-3: the plan cues (💬 instruction, 🎯 per-set) are no longer in
          // the draft — they resolve per-slug from usePlanCues, so the swap
          // shows the new exercise's cues by construction.
          // ⭐ PE-37 note, and it is the invariant that keeps this spread safe:
          // `DraftEntry.notePrescribed` IS in the draft, and this `{...entry}`
          // would carry it onto the swapped-in exercise. It never bites because
          // only `slotDraftToEntry` (the prescribe read adapter) sets it, and
          // the prescribe tree swaps through `replace_exercise` in
          // prescribeDispatch, not through this reducer. ⛔ If a logger entry
          // ever gains that field, this line has to reset it — a note about
          // squats has no business surviving onto a leg press. What still describes
          // the OLD exercise on a kept set is: the plan's PREFILL (prescribed
          // rpe/rir, AMRAP from the old slot, weight provenance vs the old
          // exercise's prescription/MAX) plus the athlete's per-set note. On a
          // NOT-YET-LOGGED set all of that is now wrong; a completed set is
          // logged history and stays untouched (never rewrite the athlete's real
          // log). A cross-kind swap blanks the sets instead. A chain-prefilled
          // weight (prescription / %MAX / history — isPrefilledWeightSource)
          // is blanked with it: it priced the OLD exercise, and on a barbell →
          // two-dumbbell swap it would silently double (40 kg bench carried
          // onto a db press reads as 40 per implement = 80 total). A weight
          // the athlete TYPED ('manual'/absent) survives — panchina occupata:
          // they planned those numbers.
          sets: isSameExercise
            ? // Only the gear they are marked on changes — which is exactly
              // what the band above the tray promises the athlete, in all five
              // locales (`swap.currentConsequence`). The tag lives on the
              // ENTRY, so the sets need no rewrite at all to carry it.
              entry.sets
            : crosses
            ? entry.sets.map((s) => emptySet(s.unit, action.loadedSides))
            : entry.sets.map((s, i) => {
                if (s.completedAt !== null) return s;
                // EQ-1-SWAP-PCTMAX — what the cell should say instead of
                // going blank, already resolved (unit converted, floored to
                // a loadable rung) by the dispatch site. `undefined` here is
                // the pre-EQ-1-SWAP-PCTMAX behaviour, unchanged.
                const carried = action.carriedWeights?.[i] ?? null;
                return {
                  ...s,
                  note: '',
                  rpe: '',
                  rir: '',
                  // BOTH halves reset, for different reasons: the
                  // prescription described the OLD slot (the new
                  // exercise must not inherit a request nobody made),
                  // and this branch only runs on completedAt === null,
                  // so there is no performed outcome to keep either.
                  amrapPrescribed: false,
                  amrap: false,
                  // ⚠️ The same rule, and it was missed once already: this
                  // branch spreads `...s`, so EVERY prescription field has to
                  // be listed here by name or it rides the swap. HI-1's time
                  // axis was the second pair to land on this shape, and for a
                  // day a swapped-in exercise inherited a 30s bout and a 90s
                  // rest nobody wrote for it — silently, because a same-kind
                  // swap raises no warning. If you add a third prescription
                  // field, it belongs in this list on the same commit.
                  workPrescribed: undefined,
                  restPrescribedSec: undefined,
                  repsRangePrescribed: undefined,
                  loadedSides: action.loadedSides,
                  ...(isPrefilledWeightSource(s.weightSource)
                    ? carried
                      ? { weight: carried.weight, extraNegative: false }
                      : { weight: '', extraNegative: false }
                    : {}),
                  weightSource:
                    isPrefilledWeightSource(s.weightSource) && carried
                      ? carried.source
                      : undefined,
                };
              }),
        };
      });
    case 'refresh_carried_weights': {
      const entry = state.entries[action.entryIdx];
      if (
        !entry ||
        entry.exerciseId !== action.exerciseId ||
        entry.sets.length !== action.expected.length
      ) {
        return state;
      }
      // Only a cell still exactly as the swap left it takes the late answer.
      let changed = false;
      const sets = entry.sets.map((s, i) => {
        const next = action.carriedWeights[i];
        if (!next || s.completedAt !== null) return s;
        const was = action.expected[i];
        const intact = was
          ? s.weight === was.weight && s.weightSource === was.source
          : s.weight === '' && s.weightSource === undefined;
        if (!intact) return s;
        changed = true;
        return {
          ...s,
          weight: next.weight,
          weightSource: next.source,
          extraNegative: false,
        };
      });
      if (!changed) return state;
      return withEntry(state, action.entryIdx, (e) => ({ ...e, sets }));
    }
    case 'add_set':
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        sets: [...entry.sets, seedNextSet(entry, action.defaultUnit)],
      }));
    case 'remove_set':
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        sets: removeAt(entry.sets, action.setIdx),
      }));
    case 'insert_set_at':
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        sets: insertAt(
          entry.sets,
          action.setIdx,
          seedInsertedSet(entry, action.setIdx, action.defaultUnit),
        ),
      }));
    case 'update_set':
      return withSet(state, action.entryIdx, action.setIdx, (set) => {
        // Flip provenance to 'manual' the moment the athlete touches
        // the weight cell — even if the new string happens to match
        // the prefilled value. The intent (athlete confirmed it) is
        // the distinction, not the bytes (Phase 1, default-weight chain).
        const sourceField =
          'weight' in action.patch ? { weightSource: 'manual' as const } : {};
        return {
          ...set,
          ...action.patch,
          ...sourceField,
        };
      });
    case 'toggle_unit':
      // ⛔ Il chip CONVERTE, non ri-etichetta (LG-48). Il perche' sta tutto in
      // convertWeightText. Qui la sola cosa locale: i passi del dropset non
      // hanno un'unita' propria, vivono in quella del set che li porta.
      return withSet(state, action.entryIdx, action.setIdx, (set) => {
        // CN-40 — `nextUnit`, not the ternary. Third and last copy.
        const to = nextUnit(set.unit);
        return {
          ...set,
          unit: to,
          weight: convertWeightText(set.weight, set.unit, to),
          ...(set.dropset === null
            ? {}
            : {
                dropset: {
                  ...set.dropset,
                  steps: set.dropset.steps.map((step) => ({
                    ...step,
                    weight: convertWeightText(step.weight, set.unit, to),
                  })),
                },
              }),
        };
      });
    case 'toggle_sign':
      return withSet(state, action.entryIdx, action.setIdx, (set) => ({
        ...set,
        extraNegative: !set.extraNegative,
      }));
    case 'cycle_set':
      // pending → done | pr → failed → pending. Same cycle the web's
      // useSetCompletion drives. The PR check (isSetPR) runs at the
      // dispatch site so prFlash arrives precomputed on the
      // pending→done transition; rest timer + audio cue stay
      // out-of-scope for the mobile MVP. The fail leg and the
      // unwind-to-pending leg both clear prFlash so the gold pill
      // doesn't linger after the user reverses a record.
      return withSet(state, action.entryIdx, action.setIdx, (set) => {
        if (set.completedAt === null) {
          return {
            ...set,
            // Live logging → wall clock. Past-session edit → the
            // dispatch site precomputes an in-window time (see
            // useSetCyclePill + timelinePlacement.ts).
            completedAt: action.completedAt ?? new Date().toISOString(),
            fail: false,
            prFlash: action.prFlash === true,
            regressionFlash: action.regressionFlash === true,
          };
        }
        if (!set.fail) {
          // done → failed. `fail` is a pure label (AP-0p model); the rep
          // decrement is a separate step done by the fail-confirm popup
          // (dispatch site), not here.
          return { ...set, ...failToggleFields(true) };
        }
        return { ...set, completedAt: null, ...failToggleFields(false) };
      });
    case 'uncomplete_set':
      // LG-67 — the timeline row's ✕. Straight to pending in ONE transition,
      // never through `failed` the way a doubled `cycle_set` would (the reason
      // lives on the action type). Values are kept on purpose: the athlete is
      // moving a set to the right exercise, not throwing the numbers away.
      // A pending set is a no-op rather than an error — the ✕ only ever renders
      // on a logged row, so reaching here twice means a double tap, not a bug.
      return withSet(state, action.entryIdx, action.setIdx, (set) =>
        set.completedAt === null
          ? set
          : { ...set, completedAt: null, ...failToggleFields(false) },
      );
    case 'cycle_dropset_step': {
      const dropset = state.entries[action.entryIdx]?.sets[action.setIdx]
        ?.dropset;
      if (!dropset) return state;
      const step = dropset.steps[action.stepIdx];
      if (!step) return state;
      const nextStep: DropsetStepDraft = {
        ...step,
        completedAt:
          step.completedAt === null ? new Date().toISOString() : null,
      };
      const steps = replaceAt(dropset.steps, action.stepIdx, nextStep);
      return withSet(state, action.entryIdx, action.setIdx, (set) => ({
        ...set,
        dropset: { ...dropset, steps },
      }));
    }
    // Single effort column (RIR | RPE per Profile.effortScale; the two
    // are the same quantity, RIR = 10 − RPE). Per-set values on
    // DraftSet.rpe / DraftSet.rir survive the column flip so re-toggling
    // restores them.
    case 'toggle_entry_effort':
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        effortColumn: action.next,
      }));
    // Group-level effort toggle (single chip on the group card, 2026-05-28).
    // Flips effortColumn for every non-cardio member of the group together,
    // so a superset can't end up with RIR on one leg and not the other.
    // Cardio members are left untouched (no per-side effort to track).
    case 'toggle_entry_history':
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        historyOpen: action.next,
      }));
    case 'set_entry_loaded_sides': {
      const nextSides = action.loadedSides;
      // Restamp pending sets so the live volume math reflects the new
      // mode immediately. Completed/failed sets keep their stamped
      // value; the user's history of what they actually lifted stays
      // honest.
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        loadedSides: nextSides,
        sets: entry.sets.map((s) =>
          s.completedAt === null ? { ...s, loadedSides: nextSides } : s,
        ),
      }));
    }
    case 'copy_history_to_entry':
      if (action.sets.length === 0) return state;
      // Universal fill rule (slice #2, 2026-05-28): position-fill only,
      // never extend. For each target position i, replace with source[i]
      // when target[i] is pending; completed/failed sets are protected;
      // trailing positions beyond min(N,M) stay untouched. Growing the
      // entry is the job of add_set / +Add round, not copy.
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        sets: entry.sets.map((targetSet, i) => {
          const sourceSet = action.sets[i];
          if (!sourceSet) return targetSet;
          if (targetSet.completedAt !== null || targetSet.fail) return targetSet;
          return sourceSet;
        }),
      }));
    case 'seed_new_entry_sets':
      // Replace the fresh entry's seed sets wholesale with the prefill
      // payload (was the `mode: 'overwrite'` short-circuit on a blank
      // entry; split out in slice #2 so copy stays purely position-fill).
      if (action.sets.length === 0) return state;
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        sets: action.sets,
      }));
    case 'hydrate':
      // Legacy-draft backfill lives in its own module (hydrateDraft.ts);
      // it's the single chokepoint for AsyncStorage rehydration + the
      // edit/repeat workflows, unrelated to the set/entry action switch.
      return hydrateDraft(action.draft);
    case 'reset':
      return initial();
  }
}
